import { Tags } from "@risalabs_frontend_org/oasis-ui-kit";
import { useEffect, useRef } from "react";
import { closeModal, Modal, openModal } from "risa-oasis-ui_v2";
import { ModalId } from "../../enums/modalId";
import {
  getCriteriaEvaluationModalContent,
  type CriteriaEvaluationStatus,
} from "../../utils/criteriaEvaluationModal";

export interface CriteriaEvaluationModalProps {
  isOpen: boolean;
  status: CriteriaEvaluationStatus;
  onInitiate: () => Promise<void>;
  onClose: () => void;
}

const DIALOG_ID = ModalId.CRITERIA_EVALUATION_MODAL;

/**
 * Displays status-specific content in a controlled Criteria Evaluation modal.
 *
 * @param props - Modal visibility, eligibility status, and close handler
 * @returns The modal when open, otherwise null
 */
export function CriteriaEvaluationModal({
  isOpen,
  status,
  onInitiate,
  onClose,
}: CriteriaEvaluationModalProps) {
  const isModalOpenRef = useRef(false);
  const content = getCriteriaEvaluationModalContent(status);

  useEffect(() => {
    if (isOpen && !isModalOpenRef.current) {
      openModal(DIALOG_ID);
      isModalOpenRef.current = true;
    } else if (!isOpen && isModalOpenRef.current) {
      closeModal(DIALOG_ID);
      isModalOpenRef.current = false;
    }
  }, [isOpen]);

  useEffect(
    () => () => {
      if (isModalOpenRef.current) {
        closeModal(DIALOG_ID);
        isModalOpenRef.current = false;
      }
    },
    [],
  );

  const handleClose = () => {
    closeModal(DIALOG_ID);
    isModalOpenRef.current = false;
    onClose();
  };

  const handleInitiate = () => {
    handleClose();
    void onInitiate().catch(() => {
      // The mutation hook exposes the user-facing error in the criteria tab.
    });
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      dialogId={DIALOG_ID}
      title="Criteria Evaluation"
      onSave={handleInitiate}
      onCancel={handleClose}
      onClose={handleClose}
      saveButtonText="Initiate"
      cancelText="Cancel"
    >
      <div className="flex flex-col gap-2 py-2">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm font-normal text-primaryGray-2">
            Eligibility Status :
          </span>
          <Tags
            label={content.tagLabel}
            backgroundColor={content.tagBackgroundColor}
            textColor={content.tagTextColor}
          />
        </div>
        <div className="m-0 text-sm font-normal leading-6 text-primaryGray-2">
          {content.message}
        </div>
      </div>
    </Modal>
  );
}

export default CriteriaEvaluationModal;
